import React from 'react';
import { Button } from '@/components/ui';
import { Edit2, UserPlus, ChevronDown, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { UserProfileDetail } from '../types/userDetail.types';
import { AuthMask } from '@/core/permissions/AuthMask';

interface UserDetailHeaderProps {
 user: UserProfileDetail;
}

export function UserDetailHeader({ user }: UserDetailHeaderProps) {
 const navigate = useNavigate();

 return (
 <div className="bg-white rounded-md p-6 border border-slate-100 mb-6">
 <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
 {/* Avatar & Basic Info */}
 <div className="flex items-center gap-6 flex-1">
 <div className="relative group">
 <div className="w-24 h-24 rounded-md bg-slate-100 border-4 border-white overflow-hidden">
 {user.avatar_path ? (
 <img src={user.avatar_path} alt="" className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center text-3xl text-slate-400 bg-slate-50">
 {user.full_name[0]}
 </div>
 )}
 </div>
 <div className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 border-4 border-white rounded-full"/>
 </div>

 <div className="flex-1">
 <div className="flex items-center gap-3 mb-1.5">
 <h1 className="text-xl text-slate-900">{user.full_name}</h1>
 <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 sm:text-sm text-xs tracking-wider border border-emerald-100">
 Đang làm việc
 </span>
 </div>
 <p className="sm:text-sm text-xs text-slate-500 tracking-wide mb-4">
 {user.position_name} • <span className="text-slate-400">{user.department_name}</span>
 </p>

 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3">
 <QuickInfoItem label="Mã nhân sự" value={user.user_code || undefined} />
 <QuickInfoItem label="Số điện thoại" value={user.phone || undefined} isMasked />
 <QuickInfoItem label="Ngày vào công ty" value={user.join_date || undefined} />
 <QuickInfoItem label="Ngày sinh" value={user.birthday || undefined} />
 <QuickInfoItem label="Email" value={user.email || undefined} isMasked />
 <QuickInfoItem label="Thâm niên" value={user.seniority || undefined} />
 <QuickInfoItem label="Giới tính" value={user.gender === 'male' ? 'Nam' : user.gender === 'female' ? 'Nữ' : 'Khác'} />
 <QuickInfoItem label="CCCD/CMND" value={user.id_number || undefined} isMasked />
 <QuickInfoItem label="Nguồn tuyển dụng" value={user.recruitment_source || undefined} />
 </div>
 </div>
 </div>

 {/* Action Buttons */}
 <div className="flex items-center gap-3 w-full lg:w-auto pt-4 lg:pt-0 border-t lg:border-t-0 border-slate-50">
 <Button 
 variant="outline" 
 className="flex-1 lg:flex-none h-10 rounded-md gap-2 sm:text-sm text-xs border-slate-200"
 onClick={() => navigate(`/users/${user.id}/edit`)}
 >
 <Edit2 className="w-3.5 h-3.5" />
 <span>Chỉnh sửa</span>
 </Button>
 <Button variant="outline" className="flex-1 lg:flex-none h-10 rounded-md gap-2 sm:text-sm text-xs border-slate-200">
 <UserPlus className="w-3.5 h-3.5" />
 <span>Tạo tài khoản</span>
 </Button>
 <Button className="flex-1 lg:flex-none bg-emerald-700 hover:bg-emerald-800 text-white h-10 rounded-md gap-2 sm:text-sm text-xs">
 <span>Thêm</span>
 <ChevronDown className="w-3.5 h-3.5" />
 </Button>
 </div>
 </div>
 </div>
 );
}

function QuickInfoItem({ label, value, isMasked }: { label: string; value?: string; isMasked?: boolean }) {
 return (
 <div className="flex flex-col gap-0.5 min-w-0">
 <span className="sm:text-sm text-xs text-slate-400 tracking-wider">{label}</span>
 {isMasked ? (
 <AuthMask>
 <span className="sm:text-sm text-xs text-slate-700 truncate">{value || '-'}</span>
 </AuthMask>
 ) : (
 <span className="sm:text-sm text-xs text-slate-700 truncate">{value || '-'}</span>
 )}
 </div>
 );
}
